import { type KeyboardEvent, type ReactElement, type ReactNode, useCallback } from 'react';
import {
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MoreHorizontal } from 'lucide-react';

export interface ContentListItemAction {
  key: string;
  label: ReactNode;
  icon?: ReactElement;
  onClick: () => void;
  colorScheme?: string;
}

interface ContentListItemProps {
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  tags?: ReactNode;
  endTags?: ReactNode;
  onClick?: () => void;
  menuActions?: ContentListItemAction[];
}

export function ContentListItem({
  title,
  description,
  icon,
  tags,
  endTags,
  onClick,
  menuActions,
}: ContentListItemProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  return (
    <HStack
      py="3"
      px="4"
      borderWidth="1px"
      borderColor="border.emphasis"
      borderRadius="lg"
      bg="bg.canvas"
      gap="4"
      w="full"
      justify="space-between"
      align="center"
      cursor={onClick ? 'pointer' : undefined}
      _hover={onClick ? { bg: 'bg.subtle' } : undefined}
      transition="background 0.15s"
      onClick={onClick ?? undefined}
      onKeyDown={onClick ? handleKeyDown : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {icon != null ? icon : null}
      <VStack align="start" spacing="1.5" flex="1" minW="0">
        <HStack spacing="2" w="full">
          <Text textStyle="sm" fontWeight="semibold" color="fg.default" noOfLines={1}>
            {title}
          </Text>
          {description != null ? (
            <Text textStyle="sm" color="fg.muted" noOfLines={1} flex="1" minW="0">
              {description}
            </Text>
          ) : null}
        </HStack>
        {tags != null ? <HStack spacing="1">{tags}</HStack> : null}
      </VStack>

      <HStack spacing="2" flexShrink={0}>
        {endTags}

        {menuActions && menuActions.length > 0 ? (
          <Menu gutter={12}>
            <MenuButton
              as={IconButton}
              aria-label="More options"
              icon={<Icon as={MoreHorizontal} />}
              size="sm"
              variant="tertiary"
              _hover={{ color: 'brand.600' }}
              _active={{ color: 'brand.600', bg: 'bg.accent.canvas' }}
              onClick={(e) => e.stopPropagation()}
            />
            <MenuList fontSize="md" onClick={(e) => e.stopPropagation()}>
              {menuActions.map((action) => (
                <MenuItem
                  key={action.key}
                  icon={action.icon}
                  onClick={action.onClick}
                  color={action.colorScheme != null ? `${action.colorScheme}.500` : undefined}
                >
                  {action.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        ) : null}
      </HStack>
    </HStack>
  );
}
